import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router";
import {
    fetchMemberTableItemsByPk,
    fetchMemberTableScan
} from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import ToggleSwitch from "../shared/ToggleSwitch";
import Select from 'react-select';

const searchOptions = [
    { value: 'pk', label: 'memberUuid (pk)', additional: { pkKey: 'memberUuid', skKey: 'tableVersion' } },
    { value: 'activationIndex', label: 'activationIndex', additional: { pkKey: 'activationStatus', skKey: 'activationCompleteDate' } },
    { value: 'certificateNumberIndex', label: 'certificateNumberIndex', additional: { pkKey: 'certificateNumber' } },
    { value: 'clientEmployeeIdIndex', label: 'clientEmployeeIdIndex', additional: { pkKey: 'clientEmployeeId', skKey: 'clientId' } },
    { value: 'clientIdPlanIdIndex', label: 'clientIdPlanIdIndex', additional: { pkKey: 'clientId', skKey: 'planId' } },
    { value: 'dobIndex', label: 'dobIndex', additional: { pkKey: 'dob', skKey: 'systemIdentifier' } },
    { value: 'dobLastNameIndex', label: 'dobLastNameIndex', additional: { pkKey: 'dob', skKey: 'lastName' } },
    { value: 'firstNameIndex', label: 'firstNameIndex', additional: { pkKey: 'firstName', skKey: 'lastName' } },
    { value: 'lastNameIndex', label: 'lastNameIndex', additional: { pkKey: 'lastName', skKey: 'firstName' } },
    { value: 'memberHccIdIndex', label: 'memberHccIdIndex', additional: { pkKey: 'memberHccId', skKey: 'systemIdentifier' } },
    { value: 'memberKeyIndex', label: 'memberKeyIndex', additional: { pkKey: 'memberKey', skKey: 'systemIdentifier' } },
    { value: 'pcpTinIndex', label: 'pcpTinIndex', additional: { pkKey: 'pcpTin', skKey: 'pcpNetwork' } },
    { value: 'systemIdentifierIndex', label: 'systemIdentifierIndex', additional: { pkKey: 'systemIdentifier', skKey: 'version' } },
    { value: 'versionIndex', label: 'versionIndex', additional: { pkKey: 'version', skKey: 'created' } },
    { value: 'versionSystemIdentifierIndex', label: 'versionSystemIdentifierIndex', additional: { pkKey: 'version', skKey: 'systemIdentifier' } },
]

const MemberTable = ({ memberUuid, autoRefreshEnabled: parentAutoRefreshEnabled }) => {

    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchPkValue, setSearchPkValue] = useState();
    const [decouncedSearchPkValue, setDecouncedSearchPkValue] = useState();
    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(parentAutoRefreshEnabled ?? false);
    const navigate = useNavigate();

    useEffect(() => {
        setAutoRefreshEnabled(parentAutoRefreshEnabled)
    }, [parentAutoRefreshEnabled])

    useEffect(() => {
        if (memberUuid) {
            setSelectedSearchOption(searchOptions[0])
            setSearchPkValue(memberUuid)
            setDecouncedSearchPkValue(memberUuid)
        }
    }, [memberUuid])

    const { ref, inView } = useInView();

    const queryKey = ['member-table', selectedSearchOption?.value, decouncedSearchPkValue, decouncedSearchSkValue];

    const {
        data,
        fetchNextPage,
        isLoading,
        isFetching,
        hasNextPage,
        isFetchingNextPage,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (selectedSearchOption?.value && selectedSearchOption.value !== 'pk') {
                params.indexName = selectedSearchOption.value
            }

            if (decouncedSearchPkValue && selectedSearchOption?.value) {
                if (decouncedSearchSkValue && selectedSearchOption?.additional?.skKey) {
                    params.skKey = selectedSearchOption.additional.skKey
                    params.skValue = decouncedSearchSkValue
                }

                return fetchMemberTableItemsByPk({
                    ...params,
                    pkKey: selectedSearchOption?.additional?.pkKey,
                    pkValue: decouncedSearchPkValue
                })
            }

            return fetchMemberTableScan(params)
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => item)).flatMap((x) => x),
                pageParams: data.pageParams,
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,
        enabled: true
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchPkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const items = data?.pages.flat();

    let columns = ['memberUuid', 'tableVersion', 'firstName', 'lastName', 'dob', 'certificateNumber', 'systemIdentifier', 'memberHccId', 'clientId', 'version', 'created'];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'memberKey', 'planId', 'pcpTin', 'pcpNetwork', 'pcpNpi', 'activationStatus', 'activationCompleteDate', 'clientEmployeeId'
        ];
    }

    return (
        <div className="container">
            {!memberUuid &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <p className="title">Member Table</p>
                        <p className="sub-title">pk: memberUuid -- sk: tableVersion | GSIs: certificateNumberIndex, clientIdPlanIdIndex, systemIdentifierIndex, versionIndex, versionSystemIdentifierIndex, firstNameIndex, lastNameIndex, activationIndex, pcpTinIndex, dobIndex, dobLastNameIndex, clientEmployeeIdIndex, memberHccIdIndex, memberKeyIndex</p>
                    </div>
                    <div>
                        <ToggleSwitch
                            label={`Auto Refresh`}
                            onToggle={(val) => setAutoRefreshEnabled(val)}
                            initialState={autoRefreshEnabled}
                        />
                    </div>
                </div>
            }

            {!memberUuid &&
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem' }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                            <Select
                                name={'searchOptions'}
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                options={searchOptions}
                                value={selectedSearchOption}
                                onChange={(option) => {
                                    setSelectedSearchOption(option);
                                    setSearchPkValue('');
                                    setDecouncedSearchPkValue('');
                                    setSearchSkValue('');
                                    setDecouncedSearchSkValue('');
                                }}
                            />

                            {selectedSearchOption?.value && <>
                                <input
                                    style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                                    type="text"
                                    id={"search"}
                                    placeholder={selectedSearchOption?.additional?.pkKey}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        setSearchPkValue(e.target.value);
                                        debouncedChangeHandler(e);
                                    }}
                                    value={searchPkValue}
                                />
                                {selectedSearchOption?.additional?.skKey &&
                                    <input
                                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                                        type="text"
                                        id={"sk"}
                                        placeholder={selectedSearchOption?.additional?.skKey}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            setSearchSkValue(e.target.value);
                                            debouncedSkChangeHandler(e);
                                        }}
                                        value={searchSkValue}
                                    />
                                }
                            </>}
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                        <p
                            style={{ cursor: "pointer", color: "var(--red)" }}
                            onClick={() => {
                                setSelectedSearchOption(null)
                                setSearchPkValue('');
                                setDecouncedSearchPkValue('');
                                setSearchSkValue('');
                                setDecouncedSearchSkValue('')
                            }}
                        >
                            Clear filters
                        </p>
                        <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                            {showMoreColumns ? "Less Columns" : "More Columns"}
                        </p>
                    </div>
                </>
            }

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"member-table"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onHistoryHandler={(row) => {
                            const params = new URLSearchParams();
                            if (row.memberUuid) params.set('memberUuid', row.memberUuid);
                            if (row.tableVersion) params.set('tableVersion', row.tableVersion);
                            navigate(`/athena/service-member?${params.toString()}`);
                        }}
                        ref={ref}
                    />
                </>
            )}
        </div>
    )
}

export default MemberTable
